<template>
    <div v-if="pending">
        <div class="d-flex justify-content-center loading-center">
            <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>
    </div>
	<div v-else class="container py-5">
        <h2>{{ $t('lifeCycleDetail') }}</h2>
        <nav aria-label="breadcrumb">
            <ol class="breadcrumb">
                <li class="breadcrumb-item">
                    <RouterLink to="/modelings">
                        {{ $t('home') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="{ name: 'modeling', params: { id: this.$route.params.id } }">
                        {{ modelingName || $t('modelings') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="'/modeling/'+ this.$route.params.id + '/assettype/' + this.$route.params.assetTypeId" >
                        {{ assetTypeName || $t('assetType') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">{{ data.name }}</li>
            </ol>
        </nav>
        <div class="row mb-4">
            <div class="col-md-4">
                <label class="form-label" for="name" required>{{ $t('name') }}</label>
                <input v-model="data.name" type="text" class="form-control" id="name" >
            </div>
              <div class="col-md-4">
                <label class="form-label" for="performanceId" required>{{ $t('performance') }}</label>
                <select class="form-select" v-model="data.performanceId">
                    <option disabled>{{ $t('select') }}</option>
                    <template v-if="performanceList.length > 0">
                        <option v-for="(performance, i) in performanceList" :key="i" v-bind:value="performance.id">{{ performance.name }}</option>
                    </template>
                </select>
            </div>
              <div class="col-md-4">
                <label class="form-label" for="description" required>{{ $t('description') }}</label>
                <input v-model="data.description" type="text" class="form-control" id="description">
            </div>
        </div>
        <div class="row mb-4">
            <div class="col-md-4">
                <label class="form-label" for="mandatoryActAttr" required>{{ $t('mandatoryActAttr') }}</label>
                <select id="mandatoryActAttr" class="form-select" v-model="data.mandatoryActAttr">
                    <option value=""></option>
                    <template v-if="datasetHeaders.length > 0" >
                        <option v-for="(datasetHeader, i) in datasetHeaders" :key="i" v-bind:value="datasetHeader.name">{{ datasetHeader.alias }}</option>
                    </template>
                    <template v-if="missingDatasetHeaders.length > 0" >
                        <option v-for="(missingDatasetHeader, i) in missingDatasetHeaders" :key="i" disabled  v-bind:value="missingDatasetHeader.name">{{ missingDatasetHeader.alias }}</option>
                    </template>
                </select>
            </div>
            <div class="col-md-4">
                <label class="form-label" for="mandatoryActYearAttr" required>{{ $t('mandatoryActYearAttr') }}</label>
                <select id="mandatoryActYearAttr" class="form-select" v-model="data.mandatoryActYearAttr">
                    <option value=""></option>
                    <template v-if="datasetHeaders.length > 0" >
                        <option v-for="(datasetHeader, i) in datasetHeaders" :key="i" v-bind:value="datasetHeader.name">{{ datasetHeader.alias }}</option>
                    </template>
                    <template v-if="missingDatasetHeaders.length > 0" >
                        <option v-for="(missingDatasetHeader, i) in missingDatasetHeaders" :key="i" disabled  v-bind:value="missingDatasetHeader.name">{{ missingDatasetHeader.alias }}</option>
                    </template>
                </select>
            </div>
            <div class="col-md-4">
                <label class="form-label" for="mandatoryActCostAttr" required>{{ $t('mandatoryActCostAttr') }}</label>
                <select id="mandatoryActCostAttr" class="form-select" v-model="data.mandatoryActCostAttr">
                    <option value=""></option>
                    <template v-if="datasetHeaders.length > 0" >
                        <option v-for="(datasetHeader, i) in datasetHeaders" :key="i" v-bind:value="datasetHeader.name">{{ datasetHeader.alias }}</option>
                    </template>
                    <template v-if="missingDatasetHeaders.length > 0" >
                        <option v-for="(missingDatasetHeader, i) in missingDatasetHeaders" :key="i" disabled  v-bind:value="missingDatasetHeader.name">{{ missingDatasetHeader.alias }}</option>
                    </template>
                </select>
            </div>
        </div>
        <button class="fa fa-eye btn btn-outline-primary float-end" @click="showTableFunction"></button>
        <div class="mb-4" v-if="showTable">
            <h4>{{ $t('lifeCycleActivities') }}</h4>
            <table class="table">
                <thead>
                    <tr>
                        <th scope="col"></th>
                        <th scope="col" class="col-2">{{ $t('activity') }}</th>
                        <th scope="col">{{ $t('minAge') }}</th>
                        <th scope="col">{{ $t('maxAge') }}</th>
                        <th scope="col">{{ $t('noAct') }}</th>
                        <th scope="col">{{ $t('signif') }}</th>
                        <th scope="col">{{ $t('effect') }}</th>
                        <th scope="col">{{ $t('notFirst') }}</th>
                        <th scope="col">{{ $t('changeToLifeCycle') }}</th>
                        <th scope="col"></th>
                        <th scope="col"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(lifeCycleActivity, i) in orderedLifeCycleActivities(data.lifeCycleActivities)" class="align-middle"  :key="i" >
                        <th scope="row"><i class="fa fa-remove" @click="removeLifeCycleActivity(i)"></i></th>
                        <td>
                            <select :id="'activityId_' + i" class="form-select" v-model="lifeCycleActivity.activityId" >
                                <option disabled>{{ $t('select') }}</option>
                                <template v-if="activityList.length > 0" >
                                    <option v-for="(activity, i) in activityList" :key="i"  v-bind:value="activity.id">{{ getActivityName(activity) }}</option>
                                </template>
                            </select>
                        </td>
                        <td>                    
                            <input v-model="lifeCycleActivity.minAge" type="number" class="form-control" :id="'minAge_' + i">
                        </td>
                        <td>
                            <input v-model="lifeCycleActivity.maxAge" type="number" class="form-control" :id="'maxAge_' + i">
                        </td>
                        <td>
                            <input v-model="lifeCycleActivity.noAct" type="number" class="form-control" :id="'noAct_' + i">
                        </td>
                        <td>
                            <input v-model="lifeCycleActivity.significant" type="number" class="form-control" :id="'significant_' + i">
                        </td>
                        <td>
                            <div class="input-group">
                                <input v-model="lifeCycleActivity.effect" type="number" class="form-control" :id="'effect_' + i">
                                <select :id="'effecttype_' + i" class="form-select" v-model="lifeCycleActivity.effectType" >
                                    <option value="0">{{ $t('Absolute') }}</option>
                                    <option value="1">{{ $t('Relative') }}</option>
                                    <option value="2">{{ $t('Differential') }}</option>
                                </select>
                            </div>
                        </td>
                        <td>
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" v-model="lifeCycleActivity.notFirst" :id="'notFirst_' + i">
                            </div>
                        </td>
                        <td>
                            <select :id="'changeToLifecycleId_' + i" class="form-select" v-model="lifeCycleActivity.changeToLifecycleId" >
                                <option selected :value=0></option>
                                <template v-if="lifeCycleList.length > 0" >
                                    <option v-for="(lifeCycle, o) in lifeCycleList" :key="o"  v-bind:value="lifeCycle.id">{{ lifeCycle.name }}</option>
                                </template>
                            </select>
                        </td>
                        <td><i v-if='i < data.lifeCycleActivities.length-1' class="fa fa-arrow-down" @click="moveActivityDown(i)"/></td>
                        <td><i v-if='i != 0' class="fa fa-arrow-up" @click="moveActivityUp(i)" /></td>
                    </tr>
                </tbody>
            </table>
            <button class="btn btn-secondary w-25 mx-auto d-block"  @click="addLifeCycleActivity"><i class="fa-solid fa-plus"></i></button>
        </div>
        <div class="mb-4" v-else>
            <h4>{{ $t('lifeCycleActivitiesChart') }}</h4>
            <apexchart class="d-flex justify-content-center" type="line" width="800px" :options="chartOptions" :series="series"></apexchart>
        </div>
        <div class="mb-3">
            <h4 class="mb-3">{{ $t('lifeCycleFilters') }} </h4>
            <table class="table">
                <thead>
                    <tr>
                        <th scope="col"></th>
                        <th scope="col">{{ $t('name') }}</th>
                        <th scope="col">{{ $t('sign') }}</th>
                        <th scope="col">{{ $t('number') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(lifeCycleFilter, i) in data.lifeCycleFilters" class="align-middle"  :key="i" >
                        <th scope="row"><i class="fa fa-remove" @click="removeLifeCycleFilter(i)"></i></th>
                        <td>
                            <select :id="'name_' + i" class="form-select" v-model="lifeCycleFilter.name">
                                <option disabled>{{ $t('select') }}</option>
                                <template v-if="datasetHeaders.length > 0" >
                                    <option v-for="(datasetHeader, i) in datasetHeaders" :key="i"  v-bind:value="datasetHeader.name">{{ datasetHeader.alias }}</option>
                                </template>
                                <template v-if="missingDatasetFilterHeaders.length > 0" >
                                    <option v-for="(missingDatasetHeader, i) in missingDatasetFilterHeaders" :key="i" disabled  v-bind:value="missingDatasetHeader.name">{{ missingDatasetHeader.alias }}</option>
                                </template>
                            </select>
                        </td>
                        <td>
                            <select v-model="lifeCycleFilter.sign" class="form-select"  :id="'sign_' + i"> 
                                <option value="=">=</option>
                                <option value=">">&gt;</option>
                                <option value="<">&lt;</option>
                                <option value=">=">&gt;=</option>
                                <option value="<=">&lt;=</option>
                            </select>
                        </td>
                        <td>
                            <input v-model="lifeCycleFilter.number" type="number" class="form-control" :id="'number_' + i">
                        </td>
                    </tr>
                </tbody>
            </table>
            <button class="btn btn-secondary w-25 mx-auto d-block"  @click="addLifeCycleFilter"><i class="fa-solid fa-plus"></i></button>
        </div>
 
        <div class="d-grid gap-2 d-md-flex ">
            <template v-if="isUserAdmin">
                <button type="button" class="btn btn-primary" @click="onSubmit">{{ $t('save') }}</button>
                <button type="button" class="btn btn-danger" @click="showDeleteLifeCycle">{{ $t('delete') }}</button>
            </template>
            <button type="button" class="btn btn-secondary" @click="$router.push({ path: `/modeling/${this.$route.params.id}/assettype/${this.$route.params.assetTypeId}` })">{{ $t('back') }}</button>
        </div>
        <LifeCycleDeleteModal v-if="showLifeCycleDeleteModal" @reload="$router.push({ path: `/modeling/${this.$route.params.id}/assettype/${this.$route.params.assetTypeId}` })" @closeDeleteDialog="showLifeCycleDeleteModal = false"/>
    </div>
</template>

<script>
import LifeCycleApi from "../../api/server/lifeCycle";
import PerformanceApi from "../../api/server/performance";
import ActivityApi from "../../api/server/activity";
import DatasetApi from "../../api/server/dataset";
import ModelingApi from "../../api/server/modeling";
import AssetTypeApi from "../../api/server/assetType";
import LifeCycleDeleteModal from './LifeCycleDeleteModal.vue';
import CheckIfUserIsAdmin from '../../authCache';

export default {
    name: "lifeCycleDetail",
    watch: {
        $route: "requestData",
    },
    components: {
        LifeCycleDeleteModal,
    },
    mounted() {
        this.requestData();
    },
    methods: {
        showTableFunction() {
            this.generateGraph();
            this.showTable = !this.showTable;
        },
        generateGraph() {
            var foundPerformance = this.performanceList.find(p => p.id == this.data.performanceId);
            var maxTheoreticalAge = 0;

            this.series = [];
            this.chartOptions.yaxis = [];
            this.chartOptions.xaxis = [];

            if(foundPerformance) {
                let seriesData = [];
                var assetPerformances = foundPerformance.performanceRanges.map(a => a.endAssetPerformance);
                var maxAssetPerformance = Math.max(...assetPerformances);

                var theoreticalAges = foundPerformance.performanceRanges.map(a => a.endTheoreticalAge);
                maxTheoreticalAge = Math.max(...theoreticalAges);

                for (let performance of foundPerformance.performanceRanges) {
                    seriesData.push({ x: performance.startTheoreticalAge, y: performance.startAssetPerformance });
                    seriesData.push({ x: performance.endTheoreticalAge, y: performance.endAssetPerformance });
                }
                this.series.push({
                    name: "Asset Condition",
                    type: "line",
                    dashed: true,
                    color: "#2E93fA",
                    data: seriesData
                });

                this.chartOptions.yaxis.push({
                    seriesName: "Asset Condition",
                    min: 0,
                    max: maxAssetPerformance,
                    tickAmount: maxAssetPerformance, // Set the number of ticks on the primary y-axis
                    labels: {
                        formatter: function (val) {
                            return val.toFixed(1); // Customize the labels on the primary y-axis
                        }
                    },
                    title: {
                        text: 'Performance'
                    },
                })
            }


            var significants = this.data.lifeCycleActivities.map(a => a.significant);
            var maxActivity = Math.max(...significants);
            var maxAges = this.data.lifeCycleActivities.map(a => a.maxAge);
            var maxActivityAge = Math.max(...maxAges);
            var ages = [];
            var theoreticalAge = 0;
            for (let age = 0; age <= maxActivityAge; age++) {
                var foundRange = foundPerformance.performanceRanges.find(a => a.startTheoreticalAge <= theoreticalAge && theoreticalAge <= a.endTheoreticalAge);
                if (foundRange) {
                    var slope = (foundRange.endAssetPerformance - foundRange.startAssetPerformance) / (foundRange.endTheoreticalAge - foundRange.startTheoreticalAge);
                    var fixedResult = (foundRange.startAssetPerformance + slope * (theoreticalAge - foundRange.startTheoreticalAge)).toFixed(2);
                    ages.push({ x: age, y: parseFloat(fixedResult) }); // Ensure the fixedResult is a number
                }
                
                theoreticalAge++;

                for(var i in this.data.lifeCycleActivities) {
                    var activity = this.data.lifeCycleActivities[i];
                    var activityWithoutEffect = (activity.minAge + activity.maxAge) / 2;
                    if (age == Math.ceil(activityWithoutEffect)){
                        var foundActivity = this.activityList.find(a => a.id == activity.activityId);
                        // selected activity is rool
                        if(foundActivity?.rootActivity) {
                            theoreticalAge = 0;
                        } else {
                            if (activity.effectType == 0) // Absolute
                                theoreticalAge = Math.round(activity.effect);
                            else if (activity.effectType == 1) // Relative
                                theoreticalAge = Math.round(theoreticalAge * activity.effect);
                            else if (activity.effectType == 2) // Differential
                                theoreticalAge = Math.round(theoreticalAge + activity.effect);
                        }
                    }
                }
            }
            this.series.push({
                name: "LC - Asset Condition",
                type: "line",
                color: "#2E93fA",
                data: ages
            });
            this.chartOptions.yaxis.push({
                seriesName: "LC - Asset Condition",
                max: maxAssetPerformance,
                tickAmount: maxAssetPerformance,
                show: false
            })

            // format years 
            this.chartOptions.xaxis = {
                tickAmount: 10,
                labels: {
                    formatter: function (value) {
                        console.log(value, "value of label")
                        return value.toFixed(); (value % 5 === 0) ? value : '';
                    }
                }
            };

            for(var i in this.data.lifeCycleActivities) {
                var activity = this.data.lifeCycleActivities[i];

                var foundActivity = this.activityList.find(a => a.id == activity.activityId);
                
                // Znázornění obdelníků dle minAge/maxAge a significant
                this.series.push({
                    name: foundActivity?.name || "Empty",
                    type: "area",
                    color: foundActivity?.activityColor || "#004C83",
                    data: [
                        { x: activity.minAge, y: activity.significant },
                        { x: activity.maxAge, y: activity.significant }
                    ]
                })

                this.chartOptions.yaxis.push({
                    seriesName: foundActivity?.name || "Empty",
                    min: 0,
                    max: maxActivity,
                    opposite: true,
                    show: i == 1,
                    title: {
                        text: "Significant"
                    }
                })

                // Pokud má noAct - rozšířím o šedou část
                if(activity.noAct) {
                    this.series.push({
                        name: "",
                        type: "area",
                        color: "#D3D3D3",
                        zIndex: 1,
                        data: [
                            { x: activity.maxAge, y: activity.significant },
                            { x: activity.maxAge + activity.noAct, y: activity.significant}
                        ]
                    })
                    this.chartOptions.yaxis.push({
                        seriesName: foundActivity?.name + '_noact' + i || "Empty",
                        min: 0,
                        max: maxActivity,
                        show: false
                    })
                }
            }

            // Určím, který prvek je dashed
            var dashArray = this.series.map(s => {
                if(s.dashed) return 4;
                return 0;
            })

            // Určím width prvků
            var widthArray = this.series.map(s => {
                if(s.dashed || s.type == "line") return 2;
                return 0;
            })

            this.chartOptions.stroke = {
                width: widthArray,
                curve: 'straight',
                dashArray: dashArray
            };
        },
        async requestData() {
            this.pending = true;
            this.isUserAdmin = await CheckIfUserIsAdmin();

            var modelingInfo = await ModelingApi.modelingInfo(this.$route.params.id);
            for(var i in modelingInfo.selections) {
                var selection = modelingInfo.selections[i];
                for(var o in selection.packages) {
                    var selectionPackage = selection.packages[o];
                    for(var u in selectionPackage.lifeCycles) {
                        var lifeCycle = selectionPackage.lifeCycles[u];
                        if(lifeCycle && lifeCycle.lifeCycleId == this.$route.params.lifeCycleId) {
                            this.foundLifeCycles.push({ 
                                selectionId: selection.id,
                                selectionPackageId: selectionPackage.id,
                                name: "Selection - " + selection.name + " - " + selectionPackage.name
                            });
                        }
                    }
                }
            }

            this.data = await LifeCycleApi.lifeCycleDetail(this.$route.params.id, this.$route.params.lifeCycleId);
            
            var performanceList = await PerformanceApi.performanceList(this.$route.params.id);
            this.performanceList = performanceList.filter(p => p.assetTypeId == this.$route.params.assetTypeId);    

            var activityList = await ActivityApi.activityList(this.$route.params.id);
            this.activityList = activityList.filter(a => a.assetTypeId == this.$route.params.assetTypeId);

            var modeling = await ModelingApi.modelingDetail(this.$route.params.id);
            this.modelingName = modeling.name;
            
            var assetType = await AssetTypeApi.assetTypeDetail(this.$route.params.id, this.$route.params.assetTypeId);
            this.assetTypeName = assetType.name;

            this.generateGraph();


            var dataset = await DatasetApi.datasetDetail(this.$route.params.id, assetType.datasetId);

            var lifeCycleList = await LifeCycleApi.lifeCycleList(this.$route.params.id);
            this.lifeCycleList = lifeCycleList.filter(l => l.assetTypeId == this.$route.params.assetTypeId  && l.id != this.$route.params.lifeCycleId);

            var datasetContent = JSON.parse(dataset.content);
            this.datasetHeaders = datasetContent["headers"];

            for(var i in this.data.lifeCycleFilters) {
                var attr = this.data.lifeCycleFilters[i];
                if(this.datasetHeaders.findIndex(d => d.name == attr.name) < 0 && this.missingDatasetFilterHeaders.findIndex(d => d.name == attr.name) < 0) {
                    this.missingDatasetFilterHeaders.push({ name: attr.name, alias: attr.name });
                }
            }

            var mandatoryActAttr = this.data.mandatoryActAttr;
            if(this.datasetHeaders.findIndex(d => d.name == mandatoryActAttr) < 0 && this.missingDatasetHeaders.findIndex(d => d.name == mandatoryActAttr) < 0) {
                this.missingDatasetHeaders.push({ name: mandatoryActAttr, alias: mandatoryActAttr });
            }

            var mandatoryActYearAttr = this.data.mandatoryActYearAttr;
            if(this.datasetHeaders.findIndex(d => d.name == mandatoryActYearAttr) < 0 && this.missingDatasetHeaders.findIndex(d => d.name == mandatoryActYearAttr) < 0) {
                this.missingDatasetHeaders.push({ name: mandatoryActYearAttr, alias: mandatoryActYearAttr });
            }

            var mandatoryActCostAttr = this.data.mandatoryActCostAttr;
            if(this.datasetHeaders.findIndex(d => d.name == mandatoryActCostAttr) < 0 && this.missingDatasetHeaders.findIndex(d => d.name == mandatoryActCostAttr) < 0) {
                this.missingDatasetHeaders.push({ name: mandatoryActCostAttr, alias: mandatoryActCostAttr });
            }

            this.pending = false;
        },
        async onSubmit() {
            if(this.data.name.length > 0) {
                if(this.data.performanceId > 0) {
                    if(!this.areLifeCycleActivitiesValid()) {
                        this.showActivitiesError();
                    } else if (!this.areLifeCycleFilterNumberAndSignValid()) {
                        this.showFiltersError();
                    } else {
                        await LifeCycleApi.lifeCycleUpdate(this.$route.params.id, this.data);

                        if(!this.isFormValid()) {
                            this.showWarning();
                        } else if (!this.areLifeCycleFiltersValid()) {
                            this.showFiltersWarning();
                        }  else {
                            this.showSuccess();
                        }
                        this.generateGraph();
                    }
                }
                else {
                    this.showMissingPerformanceWarning();
                }
            } else {
                this.showEmptyNameError();
            }
        },
        isFormValid() {
            var mandatoryActAttr = this.data.mandatoryActAttr;
            if(this.datasetHeaders.findIndex(d => d.name == mandatoryActAttr) < 0 && mandatoryActAttr != "")
                return false;

            var mandatoryActYearAttr = this.data.mandatoryActYearAttr;
            if(this.datasetHeaders.findIndex(d => d.name == mandatoryActYearAttr) < 0 && mandatoryActYearAttr != "")
                return false;

            return true;
        },
        areLifeCycleFiltersValid() {
            for(var i in this.data.lifeCycleFilters) {
                var attr = this.data.lifeCycleFilters[i];
                if(this.datasetHeaders.findIndex(d => d.name == attr.name) < 0 || attr.name == "") 
                    return false;
            }

            return true
        },
        areLifeCycleFilterNumberAndSignValid() {
            for(var i in this.data.lifeCycleFilters) {
                var attr = this.data.lifeCycleFilters[i];
                if(attr.number === "") 
                    return false;
                if(attr.sign === "") 
                    return false;
            }

            return true
        },
        areLifeCycleActivitiesValid() {
            for(var i in this.data.lifeCycleActivities) {
                var attr = this.data.lifeCycleActivities[i];
                if(attr.minAge === "") 
                    return false;
                if(attr.maxAge === "") 
                    return false;
                if(attr.noAct === "") 
                    return false;
                if(attr.significant === "") 
                    return false;
                if(attr.effect === "") 
                    return false;
                if(attr.activityId == 0)
                    return false;
            }

            return true
        },
        addLifeCycleActivity() {
            this.data.lifeCycleActivities.push({  
                activityId: 0,
                activityName: "",
                activityUnitCost: 0,
                activityCostFactor:"",
                minAge: this.getMinAge(),
                maxAge: 0,
                noAct: 0,
                significant: 0,
                effect: 0,
                effectType: 0,
                notFirst: false,
                changeToLifecycleId: 0,
                root: false,
                order: this.getActivityOrder()
            })
        },
        getMinAge() {
            var minAge = 0;
            if(this.data.lifeCycleActivities.length > 0)
                minAge = this.data.lifeCycleActivities[this.data.lifeCycleActivities.length -1].maxAge;

            return minAge;
        },
        getActivityOrder() {
            var order = 0;
            if(this.data.lifeCycleActivities.length > 0)
                order = this.data.lifeCycleActivities[this.data.lifeCycleActivities.length -1].order + 1;

            return order;
        },
        removeLifeCycleActivity(rowIndex) {
            this.data.lifeCycleActivities.splice(rowIndex, 1);
        },
        removeLifeCycleFilter(rowIndex) {
            this.data.lifeCycleFilters.splice(rowIndex, 1);
        },
        addLifeCycleFilter() {
            this.data.lifeCycleFilters.push({  
                name: "",
                sign: "=",
                number: 0
            })
        },
        getActivityName(activity) {
            return `${activity.name} ${activity.rootActivity ? " (root)" : ""}`
        },
        moveActivityUp(i) {
            var lifeCycleActivities = this.data.lifeCycleActivities;
            if (i > 0) {
                let indexOfActivityUp = i - 1;
                let tmpOrder = lifeCycleActivities[indexOfActivityUp].order;
                lifeCycleActivities[indexOfActivityUp].order = lifeCycleActivities[i].order;
                lifeCycleActivities[i].order = tmpOrder;

                lifeCycleActivities.sort((a, b) => a.order - b.order);

                this.data.lifeCycleActivities = lifeCycleActivities;
            }
        },
        moveActivityDown(i) {
            var lifeCycleActivities = this.data.lifeCycleActivities;
            if (i >= 0) {
                let indexOfActivityDown = i + 1;
                let tmpOrder = lifeCycleActivities[indexOfActivityDown].order;
                lifeCycleActivities[indexOfActivityDown].order = lifeCycleActivities[i].order;
                lifeCycleActivities[i].order = tmpOrder;

                lifeCycleActivities.sort((a, b) => a.order - b.order);

                this.data.lifeCycleActivities = lifeCycleActivities;
            }
        },
        orderedLifeCycleActivities(lifeCycleActivities) {
            return lifeCycleActivities.sort((a, b) => a.order - b.order);
        },
        showDeleteLifeCycle() {
            this.lifeCycleToDeleteId = this.data.id;
            this.showLifeCycleDeleteModal = true;
        },
           
    },
    notifications: {
        showSuccess: { 
            title: 'success',
            message: 'lifeCycleUpdated',
            type: 'success'
        },
        showWarning: { 
            title: 'warning',
            message: 'warningAttributeRemoved',
            type: 'warn'
        },
        showFiltersWarning: { 
            title: 'warning',
            message: 'filtersWarning',
            type: 'warn'
        },
        showActivitiesError: { 
            title: 'error',
            message: 'activitiesError',
            type: 'error'
        },
        showFiltersError: {
            title: 'error',
            message: 'filtersError',
            type: 'error'
        },
        showMissingPerformanceWarning: { 
            title: 'error',
            message: 'missingPerformanceWarning',
            type: 'error'
        },
        showEmptyNameError: { 
            title: 'error',
            message: 'emptyName',
            type: 'error'
        }
    },
    data() {
        return {
            datasetHeaders: [],
            missingDatasetHeaders: [],
            missingDatasetFilterHeaders: [],
            foundLifeCycles: [],
            data: {
                name: "",
                performanceId: 0,
                lifeCycleActivities: [
                    {
                        activityId: 0,
                        activityName: "",
                        activityUnitCost: 0,
                        activityCostFactor:"",
                        minAge: 0,
                        maxAge: 0,
                        noAct: 0,
                        significant: 0,
                        effect: 0,
                        notFirst: false,
                        changeToLifecycleId: 0,
                        root: false,
                        order: 0
                    }
                ],
                lifeCycleFilters: [
                    {
                        name: "",
                        sign: "",
                        number: 0
                    }
                ],
                id: 0
            },
            series: [],
            chartOptions: {
                chart: {
                    animations: {
                        enabled: false
                    },
                    height: 350,
                    type: 'line',
                    stacked: false,
                    toolbar: {
                        show: false
                    },
                }, 
                stroke: {},
                xaxis: {
                    type: 'category',
                    min: 0
                },
                dataLabels: {
                    enabled: false
                },
               
                title: {
                    text: '',
                    align: 'left',
                    offsetX: 110
                },
                yaxis: [],
                // tooltip: {
                //     fixed: {
                //         enabled: true,
                //         position: 'topLeft', // topRight, topLeft, bottomRight, bottomLeft
                //         offsetY: 30,
                //         offsetX: 60
                //     },
                // },
                //     legend: {
                //     horizontalAlign: 'left',
                //     offsetX: 40
                // }
            },
          
            lifeCycleList: [],
            performanceList: [],
            activityList: [],
            lifeCycleToDeleteId: 0,
            showLifeCycleDeleteModal: false,
            modelingName: null,
            assetTypeName: null,
            pending: true,
            showTable: true,
            isUserAdmin: false
        }
    }
};
</script>